import type { Product } from '@frontend/libs/types/product'
import type { SwiperOptions } from 'swiper/types'

import useProduct from '@frontend/libs/hooks/useProduct'
import { useMemo } from 'react'
import 'swiper/css'
import 'swiper/css/autoplay'
import 'swiper/css/free-mode'
import 'swiper/css/navigation'
import { Autoplay, FreeMode, Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { ProductItem } from '../components/product-item/ProductItem'
import ProductSliderLoader from './ProductSliderLoader'

export interface ProductSliderProps {
  categories?: null | string
  products?: Product[]
  title?: string
}

const swiperOptions: SwiperOptions = {
  autoplay: {
    delay: 6000,
    disableOnInteraction: true,
    pauseOnMouseEnter: true,
  },
  breakpoints: {
    480: { slidesPerView: 3, spaceBetween: 15 },
    768: { slidesPerView: 4, spaceBetween: 20 },
  },
  freeMode: { enabled: true, sticky: true },
  loop: true,
  modules: [Navigation, FreeMode, Autoplay],
  navigation: true,
  slidesPerView: 2,
  spaceBetween: 10,
}

function ProductSliderRenderer({
  products,
  title,
}: Readonly<ProductSliderProps>) {
  const slides = useMemo(
    () =>
      products?.map((product) => (
        <SwiperSlide key={product.id}>
          <ProductItem product={product} />
        </SwiperSlide>
      )),
    [products],
  )

  return (
    <>
      {title && (
        <h1 className="mb-4 text-xl font-medium uppercase md:text-2xl">
          {title}
        </h1>
      )}
      <Swiper {...swiperOptions} className="select-none">
        {slides}
      </Swiper>
    </>
  )
}

export function ProductSlider({
  categories,
  products: initialProducts,
  title,
}: Readonly<ProductSliderProps>) {
  const { getProduct } = useProduct()

  if (initialProducts && initialProducts.length > 0) {
    return <ProductSliderRenderer products={initialProducts} title={title} />
  }

  const { data, isLoading } = getProduct({ categories })

  if (isLoading) {
    return <ProductSliderLoader />
  }

  return <ProductSliderRenderer products={data?.data} title={title} />
}

export default ProductSlider
